
'use client';

import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { WhiteLogo } from '../white-logo';
import { useToast } from '@/hooks/use-toast';
import type { Locale } from '@/i18n-config';
import { Separator } from '../ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '../ui/spinner';
import { Checkbox } from '../ui/checkbox';
import { PhoneNumberInput } from '../ui/phone-input';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';


export function RegistrationForm({ lang, dict, redirectTo }: { lang: Locale; dict: any, redirectTo: string }) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const router = useRouter();

  const phoneRegex = new RegExp(
    /^\+[1-9]\d{1,14}$/
  );

  // Common fields for both account types
  const commonSchema = z.object({
    accountType: z.enum(['personal', 'business'], { required_error: dict.accountTypeRequired }),
    firstName: z.string().min(1, { message: dict.firstNameRequired }),
    lastName: z.string().min(1, { message: dict.lastNameRequired }),
    phoneNumber: z.string().regex(phoneRegex, { message: dict.phoneNumberInvalid }),
    email: z.string().email({ message: dict.emailInvalid }),
    password: z
      .string()
      .min(8, { message: dict.passwordMinLength })
      .regex(/[A-Z]/, { message: dict.passwordUppercaseRequired })
      .regex(/[a-z]/, { message: dict.passwordLowercaseRequired })
      .regex(/[0-9]/, { message: dict.passwordNumberRequired }),
    terms: z.boolean().refine(val => val === true, {
        message: dict.termsRequired,
    }),
  });
  
  // Fields specific to business accounts
  const businessSchema = commonSchema.extend({
      accountType: z.literal('business'),
      companyName: z.string().min(1, { message: dict.companyNameRequired }),
      industry: z.string().min(1, { message: dict.industryRequired }),
      country: z.string().min(1, { message: dict.countryRequired }),
      companyAddress: z.string().min(1, { message: dict.companyAddressRequired }),
      currency: z.string().min(1, { message: dict.currencyRequired }),
      legalEntityType: z.string().min(1, { message: dict.legalEntityTypeRequired }),
  });
  
  // Schema for personal accounts (no extra fields needed)
  const personalSchema = commonSchema.extend({
      accountType: z.literal('personal'),
  });

  // Discriminated union to handle conditional validation
  const formSchema = z.discriminatedUnion("accountType", [
    businessSchema,
    personalSchema,
  ]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      accountType: 'business',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
      password: '',
      terms: false,
    },
  });

  const { setValue, watch } = form;
  const selectedCountry = watch('country');
  const accountType = watch('accountType');

  useEffect(() => {
    if (accountType === 'business' && selectedCountry && dict.countryCurrencyMap) {
      const currencyCode = dict.countryCurrencyMap[selectedCountry];
      if (currencyCode) {
        const currencyKey = Object.keys(dict.currencies).find(
          (key) => dict.currencies[key].includes(`(${currencyCode})`)
        );
        if (currencyKey) {
          setValue('currency', currencyKey, { shouldValidate: true });
        }
      }
    }
  }, [selectedCountry, setValue, dict, accountType]);

  // Reset legal entity type when country changes
  useEffect(() => {
     if (accountType === 'business') {
        setValue('legalEntityType', '', { shouldValidate: false });
     }
  }, [selectedCountry, setValue, accountType]);
  
  const legalEntityTypes = useMemo(() => {
    if (accountType !== 'business' || !selectedCountry || !dict.legalEntityTypesByCountry) {
      return [];
    }
    const types = dict.legalEntityTypesByCountry[selectedCountry];
    return types ? Object.entries(types) : [];
  }, [selectedCountry, dict.legalEntityTypesByCountry, accountType]);


  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        const errorText = await response.text();
        // Use the server's error message if available, otherwise a generic one
        throw new Error(errorText || dict.signupFailedTitle);
      }

      // The backend returns the essential user info (uid, email) and all other data
      const userData = await response.json();

      // Store user data for the subscription step
      localStorage.setItem('userData', JSON.stringify(userData));
      
      // Redirect to the subscription page
      router.push(redirectTo);

    } catch (error: any) {
      console.error('Registration Error:', error);
      toast({
        variant: 'destructive',
        title: dict.signupFailedTitle,
        description: error.message || "An unknown error occurred. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="w-full max-w-2xl mx-auto bg-card backdrop-blur-sm border border-white/10 rounded-2xl shadow-2xl overflow-hidden my-8"
      translate="no" // Prevent browser translation interference
    >
      <div className="p-8">
        <div className="flex justify-center mb-6">
          <WhiteLogo className="w-16 h-16 text-white" />
        </div>
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-foreground">{dict.signUp}</h1>
          <p className="text-muted-foreground mt-2">
            {dict.signupDescription}
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            
            <FormField
              control={form.control}
              name="accountType"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel>{dict.accountType}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="grid grid-cols-2 gap-4"
                    >
                      <FormItem>
                        <FormControl>
                           <RadioGroupItem value="business" id="business" className="sr-only" />
                        </FormControl>
                        <FormLabel htmlFor="business" className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary">
                          {dict.accountTypes.business}
                        </FormLabel>
                      </FormItem>
                      <FormItem>
                         <FormControl>
                           <RadioGroupItem value="personal" id="personal" className="sr-only" />
                         </FormControl>
                        <FormLabel htmlFor="personal" className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary">
                          {dict.accountTypes.personal}
                        </FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Separator />
            
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-foreground">{dict.personalInfo}</h3>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{dict.firstName}</FormLabel>
                      <FormControl>
                        <Input placeholder="Max" {...field} className="h-11" disabled={isSubmitting} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{dict.lastName}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Robinson"
                          {...field}
                          className="h-11"
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{dict.email}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="m@example.com"
                        {...field}
                        className="h-11"
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
               <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{dict.phoneNumber}</FormLabel>
                      <FormControl>
                        <PhoneNumberInput
                          id="phoneNumber"
                          international
                          defaultCountry="ES"
                          className="h-11"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
            </div>
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{dict.password}</FormLabel>
                  <FormControl>
                    <Input type="password" {...field} className="h-11" disabled={isSubmitting} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
             {accountType === 'business' && (
                <>
                <Separator />
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="overflow-hidden"
                >
                    <div className="space-y-4">
                    <h3 className="text-lg font-medium text-foreground">{dict.companyInfo}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                        control={form.control}
                        name="companyName"
                        render={({ field }) => (
                            <FormItem>
                            <FormLabel>{dict.companyName}</FormLabel>
                            <FormControl>
                                <Input placeholder={dict.companyNamePlaceholder} {...field} className="h-11" disabled={isSubmitting} />
                            </FormControl>
                            <FormMessage />
                            </FormItem>
                        )}
                        />
                        <FormField
                        control={form.control}
                        name="industry"
                        render={({ field }) => (
                            <FormItem>
                            <FormLabel>{dict.industry}</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value} disabled={isSubmitting}>
                                <FormControl>
                                <SelectTrigger className="h-11">
                                    <SelectValue placeholder={dict.industryPlaceholder} />
                                </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                {Object.entries(dict.industries).map(([key, value]) => (
                                    <SelectItem key={key} value={key}>{value as string}</SelectItem>
                                ))}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                            </FormItem>
                        )}
                        />
                        <FormField
                        control={form.control}
                        name="country"
                        render={({ field }) => (
                            <FormItem>
                            <FormLabel>{dict.country}</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value} disabled={isSubmitting}>
                                <FormControl>
                                <SelectTrigger className="h-11">
                                    <SelectValue placeholder={dict.countryPlaceholder} />
                                </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                {Object.entries(dict.countries).map(([key, value]) => (
                                    <SelectItem key={key} value={key}>{value as string}</SelectItem>
                                ))}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                            </FormItem>
                        )}
                        />
                        <FormField
                        control={form.control}
                        name="currency"
                        render={({ field }) => (
                            <FormItem>
                            <FormLabel>{dict.currency}</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value} defaultValue={field.value} disabled={isSubmitting}>
                                <FormControl>
                                <SelectTrigger className="h-11">
                                    <SelectValue placeholder={dict.currencyPlaceholder} />
                                </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                {Object.entries(dict.currencies).map(([key, value]) => (
                                    <SelectItem key={key} value={key}>{value as string}</SelectItem>
                                ))}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                            </FormItem>
                        )}
                        />
                        <FormField
                        control={form.control}
                        name="companyAddress"
                        render={({ field }) => (
                            <FormItem className="md:col-span-2">
                            <FormLabel>{dict.companyAddress}</FormLabel>
                            <FormControl>
                                <Input placeholder={dict.companyAddressPlaceholder} {...field} className="h-11" disabled={isSubmitting} />
                            </FormControl>
                            <FormMessage />
                            </FormItem>
                        )}
                        />
                        <FormField
                        control={form.control}
                        name="legalEntityType"
                        render={({ field }) => (
                            <FormItem className="md:col-span-2">
                            <FormLabel>{dict.legalEntityType}</FormLabel>
                            <Select
                                onValueChange={field.onChange}
                                value={field.value}
                                disabled={!selectedCountry || legalEntityTypes.length === 0 || isSubmitting}
                            >
                                <FormControl>
                                <SelectTrigger className="h-11">
                                    <SelectValue placeholder={dict.legalEntityTypePlaceholder} />
                                </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                {legalEntityTypes.map(([key, value]) => (
                                    <SelectItem key={key} value={key}>{value as string}</SelectItem>
                                ))}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                            </FormItem>
                        )}
                        />
                    </div>
                    </div>
                </motion.div>
                </>
             )}


            <Separator />

            <FormField
              control={form.control}
              name="terms"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md p-4 border bg-muted/50">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      {dict.acceptTermsLabel}
                    </FormLabel>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
            
             <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="pt-2"
            >
            <Button
              type="submit"
              className="w-full h-11 text-base font-semibold"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? <Spinner />
                : dict.continueToPaymentButton}
            </Button>
            </motion.div>
          </form>
        </Form>

        <div className="mt-8 text-center text-sm">
          <span className="text-muted-foreground">{dict.alreadyHaveAccount} </span>
          <Link
            href={`/${lang}/login`}
            className="font-semibold text-foreground hover:underline"
          >
            {dict.signIn}
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
